import React, { useState, useEffect, useCallback, useRef } from 'react';
import OutputsAccountTable from './OutputsAccountTable';
import ForecastDropdown from "./ForecastDropdown";
import ForecastPeriodDropdown from "./ForecastPeriodDropdown";
import Box from "@mui/material/Box";
import CustomerDropdown from "./CustomerDropdown";
import ProductFiltersButton from "./ProductFiltersButton";
import ProductFilters from "./ProductFilters";
import axiosInstance from "../AxiosInstance";
import IconButton from "@mui/material/IconButton";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { Skeleton, Paper, Grid, Tabs, Tab, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { downloadCsv } from "../Utilities/csvUtility";

const ACTUALS_CHECKPOINT_MODES = {
    now: "now",
    latestOrder: "latestOrder"
};

const fetchAccountWithWeeklyYtg = async (url, params) => {
    const response = await axiosInstance.get(url, { params });
    const account = response.data.account ?? response.data;

    if (response.data.weeklyYtg || params.aggregationPeriod === "weekly") {
        return {
            account,
            weeklyYtg: response.data.weeklyYtg ?? account
        };
    }

    try {
        const weeklyResponse = await axiosInstance.get(url, {
            params: {
                ...params,
                aggregationPeriod: "weekly",
                includeWeeklyYtg: false
            }
        });
        return {
            account,
            weeklyYtg: weeklyResponse.data.account ?? weeklyResponse.data
        };
    } catch (error) {
        console.error("Failed to fetch weekly YTG source data:", error);
        return { account, weeklyYtg: account };
    }
};

const ForecastOutputsAccount = () => {
    const [forecasts, setForecasts] = useState([]);
    const [aggregationPeriod, setAggregationPeriod] = useState("monthly");
    const [selectedForecastId, setSelectedForecastId] = useState(null);
    const [selectedForecastPeriodId, setSelectedForecastPeriodId] = useState(null);
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [selectedChannelId, setSelectedChannelId] = useState(null);
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [selectedProductGroups, setSelectedProductGroups] = useState([]);
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [summaryData, setSummaryData] = useState(null);
    const [ytgSourceData, setYtgSourceData] = useState(null);
    const [error, setError] = useState(null);
    const [loadingCsv, setLoadingCsv] = useState(false);
    const [loadingForecasts, setLoadingForecasts] = useState(false);
    const [loadingTable, setLoadingTable] = useState(false);
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const [showActuals, setShowActuals] = useState(false);
    const [actualsCheckpointMode, setActualsCheckpointMode] = useState(ACTUALS_CHECKPOINT_MODES.now);
    const [loadingActuals, setLoadingActuals] = useState(false);
    const [showCompare, setShowCompare] = useState(false);
    const [isTableFullscreen, setIsTableFullscreen] = useState(false);
    const previousShowActualsRef = useRef(showActuals);
    const selectedForecast = forecasts.find((f) => Number(f.forecastId) === Number(selectedForecastId));
    const isForecastStarted = !selectedForecast?.startDate || new Date(selectedForecast.startDate) <= new Date();
    const hasValidSelection = !!(selectedForecastId && selectedForecastPeriodId !== null && selectedForecastPeriodId > 0);

    const handleFiltersToggle = () => {
        setFiltersExpanded(prev => !prev);
    };

    useEffect(() => {
        const storedForecast = JSON.parse(localStorage.getItem('selectedForecast'));
        const storedForecastPeriodId = localStorage.getItem('selectedForecastPeriodId');

        if (storedForecast?.forecastId) {
            const parsedForecastId = Number(storedForecast.forecastId);
            setSelectedForecastId(Number.isFinite(parsedForecastId) ? parsedForecastId : storedForecast.forecastId);
        }

        if (storedForecastPeriodId && storedForecastPeriodId !== "null") {
            setSelectedForecastPeriodId(parseInt(storedForecastPeriodId, 10));
        }

        fetchForecastsAndPeriods();
    }, []);

    const fetchForecastsAndPeriods = async () => {
        try {
            setLoadingForecasts(true);
            const response = await axiosInstance.get("/forecasts");
            setForecasts(response.data);
        } catch (error) {
            console.log(error);
        } finally {
            setLoadingForecasts(false);
        }
    };

    const fetchAccountSummary = useCallback(async (
        { includeActuals, showTableLoader, showActualsLoader } = { includeActuals: false, showTableLoader: true, showActualsLoader: false }
    ) => {
        if (!selectedForecastId || selectedForecastPeriodId === null) {
            if (showTableLoader) setLoadingTable(false);
            if (showActualsLoader) setLoadingActuals(false);
            return;
        }

        if (showTableLoader) setLoadingTable(true);
        if (showActualsLoader) setLoadingActuals(true);

        try {
            const params = {
                forecastPeriodId: selectedForecastPeriodId,
                customerId: selectedCustomerId,
                channelId: selectedChannelId,
                aggregationPeriod,
                includeWeeklyYtg: aggregationPeriod !== "weekly"
            };

            if (selectedProductIds && Array.isArray(selectedProductIds) && selectedProductIds.length > 0) {
                params.productIds = selectedProductIds.join(",");
            }
            if (selectedBrands && Array.isArray(selectedBrands) && selectedBrands.length > 0) {
                params.brands = selectedBrands.join(",");
            }
            if (selectedProductGroups && Array.isArray(selectedProductGroups) && selectedProductGroups.length > 0) {
                params.productGroups = selectedProductGroups.join(",");
            }
            if (includeActuals) {
                params.includeActuals = true;
                params.actualsCheckpointMode = actualsCheckpointMode;
            }

            const response = await fetchAccountWithWeeklyYtg(
                `/forecasts/${selectedForecastId}/outputs-account`,
                params);
            setSummaryData(response.account);
            setYtgSourceData(response.weeklyYtg);
            setError(null);
        } catch (error) {
            console.error("Failed to fetch account summary:", error);
            setSummaryData(null);
            setYtgSourceData(null);
            setError(error.response?.status === 404 ? "Account summary not found." : "An unexpected error occurred.");
        } finally {
            if (showTableLoader) setLoadingTable(false);
            if (showActualsLoader || includeActuals) {
                setLoadingActuals(false);
            }
        }
    }, [
        selectedForecastId,
        selectedForecastPeriodId,
        selectedCustomerId,
        selectedChannelId,
        selectedProductIds,
        selectedBrands,
        selectedProductGroups,
        aggregationPeriod,
        actualsCheckpointMode
    ]);

    useEffect(() => {
        const includeActuals = showActuals;
        const toggledActuals = previousShowActualsRef.current !== includeActuals;
        previousShowActualsRef.current = includeActuals;

        if (toggledActuals && !includeActuals) {
            setLoadingActuals(false);
            return;
        }

        fetchAccountSummary({
            includeActuals,
            showTableLoader: !toggledActuals,
            showActualsLoader: toggledActuals && includeActuals
        });
    }, [fetchAccountSummary, showActuals]);

    useEffect(() => {
        if (!isForecastStarted && showActuals) {
            setShowActuals(false);
            setShowCompare(false);
            setLoadingActuals(false);
        }
    }, [isForecastStarted, showActuals]);

    const handleForecastSelected = (id) => {
        if (id === null || id === undefined) {
            localStorage.removeItem('selectedForecast');
            localStorage.setItem('selectedForecastPeriodId', 'null');
            setSelectedForecastId(null);
            setSelectedForecastPeriodId(null);
            setError(null);
            return;
        }

        const parsedId = Number(id);
        const normalisedId = Number.isFinite(parsedId) ? parsedId : id;
        localStorage.setItem('selectedForecast', JSON.stringify({ forecastId: normalisedId }));
        setSelectedForecastId(normalisedId);
        setError(null);

        const storedForecastPeriodId = localStorage.getItem("selectedForecastPeriodId");
        const forecast = forecasts.find((f) => Number(f.forecastId) === Number(normalisedId));
        const parsedStoredPeriodId = parseInt(storedForecastPeriodId, 10);
        const validPeriod = forecast?.forecastPeriods?.some(
            (fp) => Number(fp.forecastPeriodId) === parsedStoredPeriodId
        );

        if (storedForecastPeriodId && storedForecastPeriodId !== "null" && validPeriod) {
            setSelectedForecastPeriodId(parsedStoredPeriodId);
            return;
        }

        localStorage.setItem("selectedForecastPeriodId", "null");
        setSelectedForecastPeriodId(null);
    };

    const handleCustomerSelected = (selection) => {
        setSelectedBrands([]);
        setSelectedProductGroups([]);
        setSelectedProductIds([]);
        
        if (selection.type === 'channel') {
            setSelectedChannelId(selection.id);
            setSelectedCustomerId(null);
        } else if (selection.type === 'clear') {
            setSelectedCustomerId(null);
            setSelectedChannelId(null);
        } else {
            setSelectedCustomerId(selection.id);
            setSelectedChannelId(null);
        }
    };

    const handleForecastPeriodSelected = (forecastPeriodId) => {
        if (forecastPeriodId === null || forecastPeriodId === undefined) {
            localStorage.setItem('selectedForecastPeriodId', 'null');
            setSelectedForecastPeriodId(null);
            setError(null);
            return;
        }
        localStorage.setItem("selectedForecastPeriodId", forecastPeriodId);
        setSelectedForecastPeriodId(forecastPeriodId);
        setError(null);
    };

    const handleAggregationPeriodChange = (event, newValue) => {
        setAggregationPeriod(newValue);
    };

    const handleActualsToggle = (event) => {
        const checked = event.target.checked;
        if (checked) {
            setLoadingActuals(true);
        } else {
            setLoadingActuals(false);
            setShowCompare(false);
        }
        setShowActuals(checked);
    };

    const handleCompareToggle = (event) => {
        setShowCompare(event.target.checked);
    };

    const handleActualsCheckpointModeChange = (_event, mode) => {
        if (!mode) {
            return;
        }
        setActualsCheckpointMode(mode);
    };

    const handleDownloadCsv = async () => {
        if (!selectedForecastId || selectedForecastPeriodId === null || selectedForecastPeriodId <= 0) {
            return;
        }

        try {
            const params = {
                forecastPeriodId: selectedForecastPeriodId,
                channelId: selectedChannelId,
                customerId: selectedCustomerId
            };
            
            if (selectedProductIds !== null && Array.isArray(selectedProductIds) && selectedProductIds.length > 0) {
                params.productIds = selectedProductIds.join(",");
            }
            if (selectedBrands !== null && Array.isArray(selectedBrands) && selectedBrands.length > 0) {
                params.brands = selectedBrands.join(",");
            }
            if (selectedProductGroups !== null && Array.isArray(selectedProductGroups) && selectedProductGroups.length > 0) {
                params.productGroups = selectedProductGroups.join(",");
            }
            if (showActuals) {
                params.includeActuals = true;
                params.actualsCheckpointMode = actualsCheckpointMode;
            }
            const fileName = `OutputsAccount_${selectedForecastId}_${selectedForecastPeriodId}.csv`;

            await downloadCsv(`/forecasts/${selectedForecastId}/outputs-account-csv`, params, fileName, setLoadingCsv, setError);
        } catch (error) {
            console.error("Unexpected error during CSV download:", error);
        }
    };

    const shouldShowCheckpointControls = showActuals && !loadingActuals;

    const handleToggleFullscreen = () => {
        setIsTableFullscreen(prev => !prev);
    };

    return (
        <Box 
            className="component-container"
            sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                height: '100%',
                overflow: 'hidden'
            }}
        >
            {!isTableFullscreen && (
                <Paper elevation={2} sx={{ p: 3, mb: 2, flexShrink: 0 }}>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={200} height={56} />
                        ) : (
                            <ForecastDropdown
                                forecasts={forecasts}
                                selectedForecastId={selectedForecastId}
                                onForecastSelected={handleForecastSelected}
                                width={200}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={200} height={56} />
                        ) : (
                            <ForecastPeriodDropdown
                                forecastPeriods={selectedForecast?.forecastPeriods || []}
                                selectedForecastPeriodId={selectedForecastPeriodId}
                                onForecastPeriodSelected={handleForecastPeriodSelected}
                                width={200}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={200} height={56} />
                        ) : (
                            <CustomerDropdown onCustomerSelected={handleCustomerSelected} channelSelectable={true} />
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={150} height={56} />
                        ) : (
                            <ProductFiltersButton
                                filtersExpanded={filtersExpanded}
                                onToggle={handleFiltersToggle}
                                disabled={loadingForecasts}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="circular" width={32} height={32} />
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: (theme) => theme.spacing(2) }}>
                                <Tooltip title="Download CSV">
                                    <span>
                                        <IconButton onClick={handleDownloadCsv} aria-label="download csv" disabled={loadingCsv || !hasValidSelection}>
                                            {loadingCsv ? <CircularProgress size={24} /> : <FileDownloadIcon />}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                {isForecastStarted && (
                                    <>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    size="small"
                                                    checked={showActuals}
                                                    onChange={handleActualsToggle}
                                                    disabled={loadingForecasts || loadingTable || !hasValidSelection}
                                                    inputProps={{ 'aria-label': 'toggle actuals' }} 
                                                />
                                            }
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    Actuals
                                                    {loadingActuals && <CircularProgress size={16} sx={{ ml: 1.25 }} />}
                                                </Box>
                                            }
                                            labelPlacement="end"
                                            sx={{ m: 0 }}
                                        />
                                        {shouldShowCheckpointControls && (
                                            <FormControl size="small" sx={{ minWidth: 170 }}>
                                                <InputLabel id="actuals-up-to-account-label">Up to</InputLabel>
                                                <Select
                                                    labelId="actuals-up-to-account-label"
                                                    label="Up to"
                                                    value={actualsCheckpointMode}
                                                    onChange={(event) => handleActualsCheckpointModeChange(event, event.target.value)}
                                                    inputProps={{ 'aria-label': 'up to' }}
                                                >
                                                    <MenuItem value={ACTUALS_CHECKPOINT_MODES.now}>Last week</MenuItem>
                                                    <MenuItem value={ACTUALS_CHECKPOINT_MODES.latestOrder}>Latest sales order</MenuItem>
                                                </Select>
                                            </FormControl>
                                        )}
                                        {showActuals && !loadingActuals && (
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        size="small"
                                                        checked={showCompare}
                                                        onChange={handleCompareToggle}
                                                        disabled={loadingForecasts || loadingTable || !hasValidSelection}
                                                        inputProps={{ 'aria-label': 'toggle compare' }}
                                                    />
                                                }
                                                label="Compare"
                                                labelPlacement="end"
                                                sx={{ m: 0 }}
                                            />
                                        )}
                                    </>
                                )}
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Paper>
            )}

            {!isTableFullscreen && (
                <Box sx={{ flexShrink: 0 }}>
                    <ProductFilters
                    selectedForecastId={selectedForecastId}
                    selectedForecastPeriodId={selectedForecastPeriodId}
                    selectedBrands={selectedBrands}
                    setSelectedBrands={setSelectedBrands}
                    selectedProductGroups={selectedProductGroups}
                    setSelectedProductGroups={setSelectedProductGroups}
                    selectedProductIds={selectedProductIds}
                    setSelectedProductIds={setSelectedProductIds}
                    filtersExpanded={filtersExpanded}
                    onToggle={handleFiltersToggle}
                    selectedCustomerId={selectedCustomerId}
                    selectedChannelId={selectedChannelId}
                />
                </Box>
            )}

            {isTableFullscreen && (error || loadingTable || !hasValidSelection) && (
                <Box sx={{ flexShrink: 0, mb: 1 }}>
                    <Tooltip title="Exit fullscreen">
                        <IconButton
                            onClick={handleToggleFullscreen}
                            size="small"
                            aria-label="Exit fullscreen"
                        >
                            <FullscreenExitIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            {error ? (
                <div>{error}</div>
            ) : hasValidSelection ? (
                <>
                    {!isTableFullscreen && (
                        <Paper elevation={1} sx={{ mb: 2, flexShrink: 0 }}>
                            <Tabs 
                                value={aggregationPeriod} 
                                onChange={handleAggregationPeriodChange}
                                sx={{ borderBottom: 1, borderColor: 'divider' }}
                            >
                                <Tab label="Monthly" value="monthly" />
                                <Tab label="Quarterly" value="quarterly" />
                                <Tab label="Weekly" value="weekly" />
                            </Tabs>
                        </Paper>
                    )}
                    {loadingTable ? (
                        <Skeleton variant="rectangular" width="100%" sx={{ flexGrow: 1 }} />
                    ) : (
                        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                            <OutputsAccountTable 
                                data={summaryData} 
                                ytgSourceData={ytgSourceData}
                                startMonth={summaryData?.startMonth || 1} 
                                selectedForecast={selectedForecast}
                                aggregationPeriod={aggregationPeriod}
                                showActuals={showActuals}
                                loadingActuals={loadingActuals}
                                showCompare={showCompare}
                                onToggleFullscreen={handleToggleFullscreen}
                                isFullscreen={isTableFullscreen}
                            />
                        </Box>
                    )}
                </>
            ) : null
            }
        </Box>
    );
};

export default ForecastOutputsAccount;
